import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Flame, Trophy, Target, AlertTriangle, Settings, Activity, ShieldCheck, TrendingUp, Receipt, UserPlus, CalendarCheck, Lock, ArrowUpRight, Sparkles, Pencil, Mail, ClipboardList, Share2, CheckCircle2, Circle } from "lucide-react";
import { Button, Card, Badge, ScoreRing, ProgressTrack, Metric, Logo } from "../components/kit";
import api from "../lib/api";
import { dueInfo } from "../lib/challenge";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const BADGE_ICONS = {
  first_baseline: <ShieldCheck size={16} />,
  zero_deferrals: <CalendarCheck size={16} />,
  score_60: <TrendingUp size={16} />,
  score_75: <Sparkles size={16} />,
  streak_3: <Flame size={16} />,
  streak_7: <Flame size={16} />,
  climber: <ArrowUpRight size={16} />,
  consistent: <Receipt size={16} />,
};

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [logged, setLogged] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [emailingWeekly, setEmailingWeekly] = useState(false);
  const [justLogged, setJustLogged] = useState(null); // {delta}
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const load = () => api.get("/dashboard").then((r) => setData(r.data)).catch((e) => {
    if (e.response?.status === 404) navigate("/onboarding");
    else setError("Could not load your dashboard.");
  });
  useEffect(() => { load(); }, [navigate]); // eslint-disable-line
  const [searchParams] = useSearchParams();
  const focusDim = searchParams.get("focus");
  const [celebrate, setCelebrate] = useState(false);
  useEffect(() => { if (!celebrate) return; const t = setTimeout(() => setCelebrate(false), 1400); return () => clearTimeout(t); }, [celebrate]);
  useEffect(() => {
    if (data && focusDim) {
      const el = document.querySelector(`[data-testid="dim-${focusDim}"]`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [data, focusDim]);

  if (error) return <Wrap><p style={{ color: "var(--status-risk)" }}>{error}</p></Wrap>;
  if (!data) return <Wrap><p style={{ color: "var(--text-muted)" }}>Loading your dashboard…</p></Wrap>;

  const dims = data.score.dimensions;
  const access = { has_vitals: true };
  const emailWeekly = async () => {
    setEmailingWeekly(true);
    try {
      await api.post("/vitals/weekly-report/preview");
      toast.success("Weekly report emailed", { description: "Check your inbox for this week's summary." });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't send right now.");
    } finally { setEmailingWeekly(false); }
  };
  const allAnswered = data.todays_questions.length > 0 && data.todays_questions.every((q) => logged[q.id] !== undefined);
  const checkinDone = data.checked_in_today;

  const submitCheckin = async () => {
    setSubmitting(true);
    try {
      const { data: res } = await api.post("/daily/checkin", { answers: logged });
      setJustLogged({ delta: res.delta });
      setData((d) => ({
        ...d, score: res.score, streak: res.streak, longest_streak: res.longest_streak,
        points: res.points, total_checkins: res.total_checkins, badges: res.badges,
        score_history: res.score_history, checked_in_today: true,
      }));
    } catch (e) {
      setError(e.response?.data?.detail || "Could not save your check-in.");
    } finally { setSubmitting(false); }
  };

  const earned = new Set(data.badges || []);

  return (
    <div className="buktu-root" data-brand="vitals" data-testid="dashboard-page" style={{ minHeight: "100vh", background: "var(--surface-page)" }}>
      <div className="buktu-wrap" style={{ maxWidth: 960, paddingTop: "var(--space-8)", paddingBottom: "var(--space-20)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "var(--space-3)", marginBottom: "var(--space-6)" }}>
          <Logo height={28} to="vitals" />
          <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: "var(--space-2)" }}>
            <Badge tone="accent" dot>Self-reported baseline</Badge>
            <Button variant="ghost" size="sm" data-testid="dashboard-edit-profile-btn" onClick={() => navigate("/account")} iconLeft={<Pencil size={16} />}>Edit profile</Button>
            {access.has_vitals && <Button variant="ghost" size="sm" data-testid="dashboard-weekly-report-btn" onClick={emailWeekly} disabled={emailingWeekly} iconLeft={<Mail size={16} />}>{emailingWeekly ? "Sending…" : "Email report"}</Button>}
            <Button variant="ghost" size="sm" data-testid="dashboard-logout-btn" onClick={async () => { await logout(); navigate("/"); }} iconLeft={<Settings size={16} />}>Log out</Button>
          </div>
        </div>

        <h1 style={{ font: "var(--type-title)", letterSpacing: "var(--tracking-snug)", marginBottom: "var(--space-2)" }}>
          Hi {data.display_name}{data.business_name ? <span style={{ color: "var(--text-muted)" }}> · {data.business_name}</span> : null}
        </h1>
        <p style={{ font: "var(--type-body)", color: "var(--text-muted)", marginBottom: "var(--space-6)" }}>Here's your honest read for today.</p>

        {/* locked banner */}
        {data.locked && (
          <Card padding="var(--space-5)" data-testid="dashboard-locked-banner" style={{ marginBottom: "var(--space-5)", background: "var(--status-risk-soft)", border: "1px solid transparent" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "var(--space-4)", flexWrap: "wrap" }}>
              <span style={{ color: "var(--status-risk)" }}><Lock size={22} /></span>
              <div style={{ flex: 1, minWidth: 200 }}>
                <div style={{ font: "var(--type-label)", color: "#8a2a1a" }}>Your score is paused</div>
                <div style={{ font: "var(--type-body-sm)", fontSize: 13, color: "#8a2a1a" }}>The 7-day window closed with questions unanswered. Finish your baseline to unlock daily check-ins.</div>
              </div>
              <Button size="sm" variant="accent" data-testid="dashboard-unlock-btn" onClick={() => navigate("/onboarding")}>Unlock now</Button>
            </div>
          </Card>
        )}

        {/* deferral warning */}
        {!data.locked && data.deferred_count > 0 && (
          <Card padding="var(--space-5)" data-testid="dashboard-deferred-banner" style={{ marginBottom: "var(--space-5)", background: "var(--status-watch-soft)", border: "1px solid transparent" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "var(--space-4)", flexWrap: "wrap" }}>
              <span style={{ color: "var(--status-watch)" }}><AlertTriangle size={22} /></span>
              <div style={{ flex: 1, minWidth: 200 }}>
                <div style={{ font: "var(--type-label)", color: "#7a5a00" }}>{data.deferred_count} question{data.deferred_count > 1 ? "s" : ""} still to answer</div>
                <div style={{ font: "var(--type-body-sm)", fontSize: 13, color: "#7a5a00" }}>Finish your baseline to keep your score visible past the 7-day deadline.</div>
              </div>
              <Button size="sm" data-testid="dashboard-finish-onboarding-btn" onClick={() => navigate("/onboarding")}>Finish now</Button>
            </div>
          </Card>
        )}

        <div style={{ display: "grid", gridTemplateColumns: "1.3fr 1fr", gap: "var(--space-5)", alignItems: "start" }} className="buktu-dash-grid">
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-5)" }}>
            {/* score */}
            <Card padding="var(--space-6)">
              <div style={{ display: "flex", alignItems: "center", gap: "var(--space-6)", flexWrap: "wrap" }}>
                <ScoreRing value={data.score.overall} size={128} subLabel="of 100" />
                <div style={{ display: "flex", flexDirection: "column", gap: 4, flex: 1, minWidth: 180 }}>
                  <span className="buktu-eyebrow">Business health</span>
                  <h2 style={{ font: "var(--type-heading)", fontSize: 22 }}>{verdict(data.score.overall)}</h2>
                  <span style={{ font: "var(--type-body-sm)", color: "var(--text-muted)" }}>
                    {data.stage ? data.stage.split("—")[0].trim() + " stage" : "Baseline set"}{data.constraint ? ` · watching "${data.constraint}"` : ""}
                  </span>
                  {data.score.overall !== data.baseline_overall && (
                    <span style={{ font: "var(--type-data)", fontSize: 12, color: data.score.overall >= data.baseline_overall ? "var(--status-good)" : "var(--status-risk)" }}>
                      {data.score.overall >= data.baseline_overall ? "▲" : "▼"} {Math.abs(data.score.overall - data.baseline_overall)} from baseline ({data.baseline_overall})
                    </span>
                  )}
                </div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)", marginTop: "var(--space-6)" }}>
                {data.dimensions_order.map((label) => {
                  const v = dims[label];
                  const isFocus = focusDim === label;
                  return (
                    <div key={label} style={{ display: "flex", flexDirection: "column", gap: 6, ...(isFocus ? { background: "var(--surface-brand-wash)", borderRadius: 8, padding: 10 } : {}) }} data-testid={`dim-${label}`}>
                      <div style={{ display: "flex", alignItems: "center", gap: "var(--space-3)" }}>
                        <span style={{ font: "var(--type-label)", color: "var(--text-strong)", flex: 1 }}>{label}</span>
                        <span style={{ font: "var(--type-data)", fontSize: 13, color: "var(--text-body)" }}>{v}</span>
                        <Badge tone={v >= 70 ? "good" : v >= 40 ? "watch" : "risk"} dot>{v >= 70 ? "On track" : v >= 40 ? "Watch" : "At risk"}</Badge>
                      </div>
                      <ProgressTrack value={v} tone={v >= 70 ? "good" : v >= 40 ? "watch" : "risk"} height={6} />
                      {isFocus && (
                        <div data-testid={`dim-tip-${label}`} style={{ marginTop: 6, display: "flex", flexDirection: "column", gap: 4 }}>
                          <span style={{ font: "var(--type-label)", fontSize: 12, color: "var(--buktu-green-deep)" }}>This week's checklist</span>
                          {tipFor(label).map((t, i) => (
                            <span key={i} style={{ display: "flex", alignItems: "flex-start", gap: 6, font: "var(--type-body-sm)", fontSize: 13, color: "var(--text-body)" }}>
                              <CheckCircle2 size={14} style={{ color: "var(--buktu-green)", flexShrink: 0, marginTop: 2 }} />{t}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </Card>

            {/* daily check-in */}
            <Card padding="var(--space-6)" data-testid="dashboard-checkin">
              <div style={{ display: "flex", alignItems: "center", gap: "var(--space-3)", marginBottom: "var(--space-2)" }}>
                <span style={{ color: "var(--vitals-accent)" }}><Activity size={20} /></span>
                <h3 style={{ font: "var(--type-heading)" }}>Today's check-in</h3>
                <span style={{ marginLeft: "auto", font: "var(--type-body-sm)", fontSize: 12, color: "var(--text-faint)" }}>~15 seconds · 3 of {data.daily_pool_size}</span>
              </div>
              <p style={{ font: "var(--type-body-sm)", color: "var(--text-muted)", marginBottom: "var(--space-5)" }}>
                Three questions, weighted toward {data.constraint ? `"${data.constraint}"` : "your constraint"}.
              </p>
              {data.locked ? (
                <div style={{ padding: "var(--space-5)", borderRadius: "var(--radius-md)", background: "var(--surface-page)", textAlign: "center", color: "var(--text-muted)" }}>
                  <Lock size={22} /><div style={{ font: "var(--type-body-sm)", marginTop: 6 }}>Finish your baseline to unlock daily check-ins.</div>
                </div>
              ) : checkinDone ? (
                <div style={{ padding: "var(--space-5)", borderRadius: "var(--radius-md)", background: "var(--surface-brand-wash)", textAlign: "center" }}>
                  <div style={{ color: "var(--buktu-green)", marginBottom: 6 }}><Trophy size={24} /></div>
                  <div style={{ font: "var(--type-heading)", color: "var(--buktu-green-deep)" }}>Logged for today.</div>
                  <div style={{ font: "var(--type-body-sm)", fontSize: 13, color: "var(--text-muted)" }}>
                    {justLogged ? (justLogged.delta > 0 ? `Score nudged up ${justLogged.delta}. ` : justLogged.delta < 0 ? `Score dipped ${Math.abs(justLogged.delta)}. ` : "Score held steady. ") : ""}
                    Come back tomorrow to keep your {data.streak}-day streak alive.
                  </div>
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-5)" }}>
                  {data.todays_questions.map((q) => (
                    <DailyQuestion key={q.id} q={q} value={logged[q.id]} onAnswer={(v) => setLogged((l) => ({ ...l, [q.id]: v }))} />
                  ))}
                  <Button data-testid="dashboard-checkin-submit" disabled={!allAnswered || submitting} onClick={submitCheckin}>
                    {submitting ? "Saving…" : allAnswered ? "Log today's check-in" : "Answer all three to log"}
                  </Button>
                </div>
              )}
            </Card>
          </div>

          {/* right column */}
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-5)" }}>
            {access.has_vitals && (<>
            <Card padding="var(--space-6)">
              <div style={{ display: "flex", gap: "var(--space-6)", alignItems: "center" }}>
                <Metric label="Current streak" value={data.streak} unit={data.streak === 1 ? "day" : "days"} note={data.longest_streak > data.streak ? `Best: ${data.longest_streak}` : "Keep it going"} />
                <div style={{ width: 1, alignSelf: "stretch", background: "var(--border-hairline)" }} />
                <Metric label="Points" value={data.points} note={`${data.total_checkins} check-in${data.total_checkins === 1 ? "" : "s"}`} />
              </div>
              {data.score_history && data.score_history.length > 1 && <Sparkline history={data.score_history} />}
            </Card>

            <Card tone="wash" padding="var(--space-5)">
              <div style={{ display: "flex", alignItems: "center", gap: "var(--space-4)" }}>
                <span style={{ width: 44, height: 44, borderRadius: "var(--radius-md)", background: "var(--paper-white)", display: "grid", placeItems: "center", color: "var(--vitals-accent)", flexShrink: 0 }}><Target size={22} /></span>
                <div style={{ flex: 1 }}>
                  <span style={{ font: "var(--type-label)", color: "var(--buktu-green-deep)" }}>This week's quest</span>
                  <div style={{ font: "var(--type-body-sm)", fontSize: 13, color: "var(--text-muted)" }}>{questFor(data.constraint)}</div>
                </div>
              </div>
            </Card>

            <Card padding="var(--space-6)">
              <h3 style={{ font: "var(--type-heading)", marginBottom: "var(--space-4)" }}>Badges <span style={{ font: "var(--type-body-sm)", fontSize: 12, color: "var(--text-faint)" }}>{earned.size}/{data.all_badges.length}</span></h3>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--space-3)" }}>
                {data.all_badges.map((b) => {
                  const on = earned.has(b.id);
                  return (
                    <div key={b.id} data-testid={`badge-${b.id}`} style={{ display: "flex", alignItems: "center", gap: "var(--space-3)", padding: "var(--space-3)", borderRadius: "var(--radius-md)", background: on ? "var(--surface-brand-wash)" : "var(--surface-card)", border: "1px solid " + (on ? "transparent" : "var(--border-hairline)"), opacity: on ? 1 : 0.5 }}>
                      <span style={{ width: 32, height: 32, borderRadius: 999, display: "grid", placeItems: "center", background: on ? "var(--buktu-green)" : "var(--ink-100)", color: on ? "#fff" : "var(--text-faint)", flexShrink: 0 }}>{BADGE_ICONS[b.id] || <ShieldCheck size={16} />}</span>
                      <span style={{ font: "var(--type-body-sm)", fontSize: 12, color: on ? "var(--buktu-green-deep)" : "var(--text-muted)" }}>{b.name}</span>
                    </div>
                  );
                })}
              </div>
            </Card>
            </>)}

            <Card tone="deep" padding="var(--space-6)">
              <span className="buktu-eyebrow" style={{ color: "var(--buktu-sage)" }}>Anonymous cohort</span>
              <h3 style={{ font: "var(--type-heading)", color: "#fff", margin: "6px 0 var(--space-4)" }}>{data.business_name ? "Your peer group" : "Similar businesses"}</h3>
              <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
                {(data.peer && data.peer.count >= 10 ? [["You", data.score.overall, true], ["Cohort average", data.peer.avg, false], ["Top quartile", data.peer.top_quartile, false]] : [["You", data.score.overall, true], ["Cohort median", 64, false], ["Top quartile", 82, false]]).map(([label, v, me]) => (
                  <div key={label} style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", font: "var(--type-label)", fontSize: 13, color: "#E4EDEA" }}>
                      <span>{label}</span><span style={{ font: "var(--type-data)", fontSize: 12 }}>{v}</span>
                    </div>
                    <div style={{ height: 8, borderRadius: 999, background: "rgba(255,255,255,0.14)" }}>
                      <div style={{ width: v + "%", height: "100%", borderRadius: 999, background: me ? "var(--vitals-accent)" : "var(--buktu-sage)", transition: "width var(--dur-slow) var(--ease-out)" }} />
                    </div>
                  </div>
                ))}
                <span style={{ font: "var(--type-body-sm)", fontSize: 12, color: "var(--text-muted)" }}>{data.peer && data.peer.count >= 10 ? `Based on ${data.peer.count} anonymized Vitals baselines.` : "Cohorts need at least 30 businesses before a real comparison. Sample figures for now."}</span>
              </div>
            </Card>
          </div>
        </div>
        {data.has_coach && ((data.coach_is_pro && data.coach_custom_metrics?.length > 0) || data.coach_challenges?.length > 0) && (
          <div data-testid="owner-coach-section" style={{ display: "flex", flexDirection: "column", gap: "var(--space-5)", marginTop: "var(--space-5)" }}>
            {data.coach_is_pro && data.coach_custom_metrics?.length > 0 && <CoachQuestionsCard metrics={data.coach_custom_metrics} />}
            {data.coach_challenges?.length > 0 && <OwnerChallengesCard challenges={data.coach_challenges} privacy={data.coach_privacy} onChange={load} onCelebrate={() => setCelebrate(true)} />}
          </div>
        )}
      </div>
      <style>{`@media (max-width:860px){.buktu-dash-grid{grid-template-columns:1fr !important;}}`}</style>
      {celebrate && <Confetti />}
    </div>
  );
}

function CoachQuestionsCard({ metrics }) {
  const [vals, setVals] = useState(() => Object.fromEntries(metrics.map((m) => [m.id, m.value || ""])));
  const [saving, setSaving] = useState(false);
  const set = (id, v) => setVals((s) => ({ ...s, [id]: v }));
  const save = async () => {
    setSaving(true);
    try {
      await Promise.all(metrics.map((m) => api.post("/owner/custom-metrics/answer", { metric_id: m.id, value: String(vals[m.id] ?? "") })));
      toast.success("Shared with your coach", { description: "Your coach can see these only if you've opted in to sharing." });
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't save."); }
    finally { setSaving(false); }
  };
  return (
    <Card padding="var(--space-6)" data-testid="owner-custom-questions">
      <div style={{ display: "flex", alignItems: "center", gap: "var(--space-3)", marginBottom: "var(--space-2)" }}>
        <span style={{ color: "var(--vitals-accent)" }}><ClipboardList size={20} /></span>
        <h3 style={{ font: "var(--type-heading)", flex: 1 }}>Your coach's questions</h3>
        <Badge tone="accent"><Share2 size={11} style={{ marginRight: 4 }} />Shared with your coach</Badge>
      </div>
      <p style={{ font: "var(--type-body-sm)", fontSize: 12, color: "var(--text-faint)", marginBottom: "var(--space-5)" }}>These answers are visible to your coach only while your sharing is turned on in Account privacy.</p>
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
        {metrics.map((m) => (
          <div key={m.id} data-testid={`owner-metric-${m.id}`} style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
            <span style={{ font: "var(--type-label)", color: "var(--text-strong)" }}>{m.label}</span>
            {m.type === "yesno" ? (
              <div style={{ display: "flex", gap: "var(--space-2)" }}>
                {["Yes", "No"].map((o) => (
                  <button key={o} type="button" className="buktu-option" data-selected={vals[m.id] === o} data-testid={`owner-metric-${m.id}-opt-${o}`} onClick={() => set(m.id, o)} style={{ width: "auto", padding: "10px 18px" }}>{o}</button>
                ))}
              </div>
            ) : m.type === "select" ? (
              <select className="buktu-input" data-testid={`owner-metric-${m.id}-select`} value={vals[m.id] || ""} onChange={(e) => set(m.id, e.target.value)}>
                <option value="">Choose…</option>
                {(m.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            ) : (
              <input className="buktu-input" data-testid={`owner-metric-${m.id}-input`} type={m.type === "number" ? "number" : m.type === "date" ? "date" : "text"} value={vals[m.id] || ""} onChange={(e) => set(m.id, e.target.value)} placeholder="Your answer" />
            )}
            {(m.type === "number" || m.type === "yesno") && m.target != null && (
              <span data-testid={`owner-metric-target-${m.id}`} style={{ font: "var(--type-body-sm)", fontSize: 12, color: "var(--text-faint)" }}>
                <Target size={11} style={{ verticalAlign: -1, marginRight: 3 }} />Coach's target: {m.type === "yesno" ? `${m.target}% Yes` : m.target}
                {m.type === "number" && (vals[m.id] ?? "") !== "" && !isNaN(Number(vals[m.id])) && (
                  <span style={{ marginLeft: 6, color: Number(vals[m.id]) >= m.target ? "var(--status-good)" : "var(--status-risk)" }}>
                    · you: {vals[m.id]} {Number(vals[m.id]) >= m.target ? "▲" : "▼"}
                  </span>
                )}
              </span>
            )}
          </div>
        ))}
      </div>
      <Button data-testid="owner-metrics-save" onClick={save} disabled={saving} style={{ marginTop: "var(--space-5)" }}>{saving ? "Saving…" : "Save and share"}</Button>
    </Card>
  );
}

function OwnerChallengesCard({ challenges, privacy, onChange, onCelebrate }) {
  const navigate = useNavigate();
  const join = async (id) => { try { await api.post(`/owner/challenges/${id}/join`); toast.success("You're in the challenge"); onChange(); } catch (e) { toast.error(e.response?.data?.detail || "Couldn't join."); } };
  const toggle = async (id) => { try { const { data } = await api.post(`/owner/challenges/${id}/complete`); if (data.completed) { const s = data.streak || 0; toast.success(s >= 2 ? `On a roll — ${s}-week streak!` : "Marked complete 🎉"); if (s >= 2 && onCelebrate) onCelebrate(s); } else { toast.success("Marked incomplete"); } onChange(); } catch (e) { toast.error(e.response?.data?.detail || "Couldn't update."); } };
  return (
    <Card padding="var(--space-6)" data-testid="owner-challenges">
      <div style={{ display: "flex", alignItems: "center", gap: "var(--space-3)", marginBottom: "var(--space-2)" }}>
        <span style={{ color: "var(--vitals-accent)" }}><Flame size={20} /></span>
        <h3 style={{ font: "var(--type-heading)", flex: 1 }}>Cohort challenges</h3>
      </div>
      {privacy === "full" ? (
        <p style={{ font: "var(--type-body-sm)", fontSize: 12, color: "var(--text-faint)", marginBottom: "var(--space-5)" }}>Your name appears on your coach's leaderboard because sharing is on.</p>
      ) : (
        <div data-testid="leaderboard-nudge" style={{ display: "flex", alignItems: "center", gap: "var(--space-3)", flexWrap: "wrap", padding: "var(--space-3) var(--space-4)", borderRadius: "var(--radius-md)", background: "var(--surface-brand-wash)", marginBottom: "var(--space-5)" }}>
          <span style={{ color: "var(--buktu-green)" }}><Trophy size={18} /></span>
          <span style={{ flex: 1, minWidth: 180, font: "var(--type-body-sm)", fontSize: 13, color: "var(--buktu-green-deep)" }}>You're anonymous on the leaderboard. Reveal your name to be recognised as you climb it.</span>
          <Button size="sm" variant="secondary" data-testid="leaderboard-reveal-btn" onClick={() => navigate("/account")}>Reveal my name</Button>
        </div>
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
        {challenges.map((c) => {
          const di = dueInfo(c.due_date);
          return (
          <div key={c.id} data-testid={`owner-challenge-${c.id}`} style={{ display: "flex", alignItems: "center", gap: "var(--space-3)", padding: "var(--space-3)", borderRadius: "var(--radius-md)", border: "1px solid var(--border-hairline)" }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <span style={{ font: "var(--type-label)", color: "var(--text-strong)" }}>{c.title}</span>
                {di && <Badge tone={di.tone} dot data-testid={`owner-challenge-due-${c.id}`}>{di.label}</Badge>}
              </div>
              {c.description && <div style={{ font: "var(--type-body-sm)", fontSize: 12, color: "var(--text-muted)" }}>{c.description}</div>}
              <div style={{ font: "var(--type-body-sm)", fontSize: 11, color: "var(--text-faint)", marginTop: 4 }}>{c.enrolled} enrolled · {c.completed_count} completed</div>
            </div>
            {!c.joined ? (
              <Button size="sm" data-testid={`owner-challenge-join-${c.id}`} onClick={() => join(c.id)}>Join</Button>
            ) : (
              <button type="button" data-testid={`owner-challenge-toggle-${c.id}`} onClick={() => toggle(c.id)} style={{ display: "inline-flex", alignItems: "center", gap: 6, border: 0, background: "transparent", cursor: "pointer", font: "var(--type-label)", fontSize: 13, color: c.completed ? "var(--status-good)" : "var(--text-muted)" }}>
                {c.completed ? <CheckCircle2 size={18} /> : <Circle size={18} />}{c.completed ? "Completed" : "Mark done"}
              </button>
            )}
          </div>
        );})}
      </div>
    </Card>
  );
}

const DIMENSION_TIPS = {
  "Financial Health": ["Review the pricing on your top offer", "Chase any overdue invoices this week", "Check your 30-day cash runway"],
  "Marketing Engine": ["Pick one channel to focus on", "Post or publish 3 times this week", "Track which post drove the most interest"],
  "Sales & Conversion": ["Follow up every open lead within 24h", "Write down your current close rate", "Ask one happy client for a referral"],
  "Operations": ["Document one repeatable process", "Automate or delegate one recurring task", "List your biggest time-sink to fix next"],
  "Team & Leadership": ["Delegate one task off your plate", "Set one clear weekly check-in", "Give one piece of specific feedback"],
  "Offer & Value": ["State the one outcome your offer delivers", "Remove one feature that adds confusion", "Test the offer with one new prospect"],
  "Growth Readiness": ["Set one measurable 90-day goal", "Pick the single metric that matters most", "Block 2 hours to work ON the business"],
};
const tipFor = (d) => DIMENSION_TIPS[d] || ["Pick one small action to improve this", "Do it before your next check-in", "Note what changed next week"];

function Confetti() {
  const colors = ["#106958", "#e4b429", "#2f80ed", "#eb5757", "#27ae60"];
  const pieces = Array.from({ length: 36 });
  return (
    <div data-testid="confetti" style={{ position: "fixed", inset: 0, pointerEvents: "none", zIndex: 9999, overflow: "hidden" }}>
      {pieces.map((_, i) => {
        const left = Math.random() * 100, delay = Math.random() * 0.3, dur = 0.9 + Math.random() * 0.6, size = 6 + Math.random() * 6;
        return <span key={i} style={{ position: "absolute", top: "-5%", left: `${left}%`, width: size, height: size * 0.5, background: colors[i % colors.length], borderRadius: 1, animation: `buktu-confetti ${dur}s ${delay}s ease-in forwards`, opacity: 0.9 }} />;
      })}
      <style>{`@keyframes buktu-confetti{to{transform:translateY(110vh) rotate(720deg);opacity:0;}}`}</style>
    </div>
  );
}

function Sparkline({ history }) {
  const vals = history.map((h) => h.overall);
  const min = Math.min(...vals), max = Math.max(...vals);
  const range = Math.max(1, max - min);
  const w = 220, h = 40;
  const pts = vals.map((v, i) => `${(i / (vals.length - 1)) * w},${h - ((v - min) / range) * h}`).join(" ");
  return (
    <div style={{ marginTop: "var(--space-4)" }} data-testid="score-sparkline">
      <span className="buktu-eyebrow">Score trend</span>
      <svg width="100%" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" style={{ display: "block", marginTop: 6, height: 40 }}>
        <polyline points={pts} fill="none" stroke="var(--vitals-accent)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </div>
  );
}

function DailyQuestion({ q, value, onAnswer }) {
  let options = ["Yes", "No"];
  if (q.type === "binary") options = ["ON the business", "IN it"];
  if (q.type === "stepper") options = ["0", "1", "2", "3+"];
  return (
    <div data-testid={`daily-${q.id}`}>
      <div style={{ font: "var(--type-label)", color: "var(--text-strong)", marginBottom: "var(--space-2)" }}>{q.label}</div>
      <div style={{ display: "flex", gap: "var(--space-2)", flexWrap: "wrap" }}>
        {options.map((o) => (
          <button key={o} type="button" className="buktu-option" data-selected={value === o} data-testid={`daily-${q.id}-opt-${o}`} onClick={() => onAnswer(o)} style={{ width: "auto", padding: "10px 18px" }}>
            {o}
          </button>
        ))}
      </div>
    </div>
  );
}

function verdict(s) {
  if (s >= 75) return "Strong and steady";
  if (s >= 60) return "Building momentum";
  if (s >= 45) return "Finding footing";
  return "Needs attention";
}
function questFor(constraint) {
  const map = {
    Leads: "Do outreach or publish content four days this week · +8 pts",
    "Close rate": "Log every sales conversation for five days · +8 pts",
    "Delivery capacity": "Clear one overdue delivery and mark it done · +8 pts",
    Cash: "Make one upsell offer or price raise this week · +8 pts",
    Team: "Delegate one task you usually do yourself · +8 pts",
    "My own time": "Spend two days working ON the business, not in it · +8 pts",
  };
  return map[constraint] || "Check in three days in a row · +8 pts";
}

function Wrap({ children }) {
  return <div className="buktu-root" style={{ minHeight: "100vh", display: "grid", placeItems: "center" }}>{children}</div>;
}
